import LottiePlayer from 'components/global/LottiePlayer';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { faBarcode, faCalendarAlt, faQrcode, faRoute, faTruck, faUser } from '@fortawesome/pro-solid-svg-icons';

import InfoLabel from './component/InfoLabel';
import * as S from './styled/OrderTracking1.styled';

/**
 * @author Burak Ak
 */

export const orderTracking1DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#2ecc71',
		name: 'headerBackgroundColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'headerTextColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTitleTextColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'iconColor'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showCustomerName'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showDeliveryCompany'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderDate'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showTrackingNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showTrackingUrl'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showIcons'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author Burak Ak
 */

const OrderTracking1 = (props) => {
	const { customerName, deliveryCompany, orderDate, orderNumber, trackingNumber, trackingURL, status, propJson } =
		props;

	const { getLocaleDate } = useLocale();

	const lottieStatus = (index) => {
		switch (status) {
			case 'returned':
			case 'confirmedReturn':
			case 'canceled':
			case 'receivedOrder':
				return index > 0 ? 'grayScale' : '';
			case 'arrivedOriginTerminal':
			case 'arrivedTerminal':
			case 'pickedUp':
			case 'arrivedDestinationTerminal':
			case 'inTransit':
			case 'departedTerminal':
			case 'processing':
			case 'arrivedDestination':
				return index > 1 ? 'grayScale' : '';
			case 'outForDelivery':
				return index > 2 ? 'grayScale' : '';
			case 'delivered':
				return index > 3 ? 'grayScale' : '';
			default:
				return '';
		}
	};

	const getImageNumber = () => {
		switch (status) {
			case 'returned':
			case 'confirmedReturn':
			case 'canceled':
			case 'receivedOrder':
				return 1;
			case 'arrivedOriginTerminal':
			case 'arrivedTerminal':
			case 'pickedUp':
			case 'arrivedDestinationTerminal':
			case 'inTransit':
			case 'departedTerminal':
			case 'processing':
			case 'arrivedDestination':
				return 2;
			case 'outForDelivery':
				return 3;
			case 'delivered':
				return 4;
			default:
				return 1;
		}
	};

	return (
		<div>
			<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
				<S.TrackingContainer>
					<div className="passive-stage">
						<div className={`lottie-first ${lottieStatus(0)}`}>
							<LottiePlayer name="orderTracking1-1" speed={0.7} width="auto" height="auto" />
						</div>
						<div className={`lottie-second ${lottieStatus(1)}`}>
							<LottiePlayer name="orderTracking1-2" speed={0.7} width="auto" height="auto" />
						</div>
						<div className={`lottie-third ${lottieStatus(2)}`}>
							<LottiePlayer name="orderTracking1-3" speed={0.7} width="auto" height="auto" />
						</div>
						<div className={`lottie-fourth ${lottieStatus(3)}`}>
							<LottiePlayer name="orderTracking1-4" speed={0.7} width="auto" height="auto" />
						</div>
						<S.TrackingImage
							url={`https://storage.googleapis.com/oto-files/OrderTracking/stage${getImageNumber()}-${getImageNumber()}.svg`}
							alt="stage-1"
						/>
					</div>
				</S.TrackingContainer>
			</S.Card>
			<S.Triangle headerBackgroundColor={propJson?.headerBackgroundColor} />
			<S.Card formBackgroundColor={propJson?.formBackgroundColor}>
				<S.StatusContainer headerBackgroundColor={propJson?.headerBackgroundColor}>
					<Typography
						variant="p1"
						fontWeight="500"
						color={propJson?.headerTextColor ? propJson?.headerTextColor : 'white'}>
						{status}
					</Typography>
				</S.StatusContainer>

				<S.TrackingContent>
					<div className="row row-cols-1 row-cols-lg-2">
						{propJson?.showOrderNumber && (
							<InfoLabel
								label="orderNumber"
								value={orderNumber}
								icon={propJson?.showIcons ? faBarcode : null}
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}

						{propJson?.showCustomerName && (
							<InfoLabel
								label="customerName"
								value={customerName}
								icon={propJson?.showIcons ? faUser : null}
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showDeliveryCompany && (
							<InfoLabel
								label="deliveryCompany"
								value={deliveryCompany}
								valueFontWeight="700"
								icon={propJson?.showIcons ? faTruck : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showOrderDate && (
							<InfoLabel
								label="orderDate"
								value={getLocaleDate(orderDate)}
								valueFontWeight="700"
								icon={propJson?.showIcons ? faCalendarAlt : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showTrackingNumber && (
							<InfoLabel
								label="trackingNumber"
								anchor={trackingNumber}
								valueFontWeight="700"
								anchorLink={trackingURL}
								icon={propJson?.showIcons ? faQrcode : null}
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
							/>
						)}
						{propJson?.showTrackingUrl && (
							<InfoLabel
								label="trackingURL"
								icon={propJson?.showIcons ? faRoute : null}
								anchor="clickHereToTrackOrder"
								valueFontWeight="700"
								labelColor={propJson?.formTitleTextColor}
								valueColor={propJson?.formTextColor}
								iconColor={propJson?.iconColor}
								anchorLink={trackingURL}
							/>
						)}
					</div>
				</S.TrackingContent>
			</S.Card>
		</div>
	);
};

export default OrderTracking1;
